import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Activity,
  RefreshCw,
  AlertCircle,
  Phone,
  CheckCircle,
  ArrowRightLeft,
  Users,
  UserCheck,
  UserX,
  DollarSign,
  Link2,
  Smile,
  FileX2,
  Gift,
  Calendar
} from 'lucide-react';
import { LoadingSpinner } from 'components/common';
import api from '../../../../services/api';
import {
  MOVFIT_DASHBOARD_AUTOMACOES_WEBHOOK,
  formatMesReferencia,
  formatValorMetricaDisplay,
  mesReferenciaAtualYm,
  parseDashboardAutomacoesMovFit
} from '../../utils/movfitDashboardAutomacoesWebhook';

const MOVFIT_IA_CANCELAMENTOS_WEBHOOK =
  (typeof process !== 'undefined' &&
    process.env &&
    process.env.REACT_APP_MOVFIT_IA_CANCELAMENTOS_WEBHOOK) ||
  'https://n8n2.mov.pro.br/webhook/cancelamento_movfit_ia';

/** Extrai total de solicitações de cancelamento do JSON do n8n (`resumo.totalCancelamentos`). */
function parseTotalCancelamentosIa(payload) {
  if (payload == null) return null;
  let root = payload;
  if (Array.isArray(root) && root.length > 0) {
    root = root[0];
  }
  if (root?.data != null && typeof root.data === 'object' && !Array.isArray(root.data)) {
    root = root.data;
  }
  const raw = root?.resumo?.totalCancelamentos ?? root?.totalCancelamentos;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** Chaves em `nps_por_unidade` do webhook — alinhadas ao retorno do n8n. */
const UNIDADES_SATISFACAO = [
  { key: 'stm_24h', nome: 'Santarém 24 horas' },
  { key: 'stm_nova_republica', nome: 'Santarém Nova República' },
  { key: 'medicilandia', nome: 'Medicilândia' },
  { key: 'itaituba', nome: 'Itaituba' }
];

const parseNpsCount = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

/**
 * Monta métricas de satisfação a partir de um bloco do webhook
 * (nota_1__nps … nota_5__nps, total_avaliacoes_nps, media_nps).
 */
function computeNpsMetrics(npsFonte) {
  const fonte = npsFonte && typeof npsFonte === 'object' ? npsFonte : {};

  const nota5 = parseNpsCount(fonte.nota_5__nps);
  const nota4 = parseNpsCount(fonte.nota_4__nps);
  const nota3 = parseNpsCount(fonte.nota_3__nps);
  const nota2 = parseNpsCount(fonte.nota_2__nps);
  const nota1 = parseNpsCount(fonte.nota_1__nps);

  const totalNotasCalculado =
    parseNpsCount(fonte.total_avaliacoes_nps) || nota1 + nota2 + nota3 + nota4 + nota5;
  const totalNotas = totalNotasCalculado || 0;

  const getPercent = (valor) => {
    if (!totalNotas || totalNotas <= 0) return 0;
    return (valor / totalNotas) * 100;
  };

  const mediaNpsValor =
    typeof fonte.media_nps === 'number'
      ? fonte.media_nps
      : Number(fonte.media_nps) || null;

  const detratores = nota1 + nota2;
  const passivos = nota3;
  const promotores = nota4 + nota5;

  const percDetratores = getPercent(detratores);
  const percPassivos = getPercent(passivos);
  const percPromotores = getPercent(promotores);

  const promotoresStop = percPromotores;
  const passivosStop = percPromotores + percPassivos;

  const npsRingBackground =
    totalNotas > 0
      ? `conic-gradient(#22c55e 0% ${promotoresStop}%, #eab308 ${promotoresStop}% ${passivosStop}%, #ef4444 ${passivosStop}% 100%)`
      : 'conic-gradient(#e5e7eb 0% 100%)';

  const mediaNpsLabel =
    mediaNpsValor == null
      ? ''
      : mediaNpsValor >= 4.5
        ? 'Excelente'
        : mediaNpsValor >= 4
          ? 'Muito bom'
          : mediaNpsValor >= 3
            ? 'Neutro'
            : 'Crítico';

  const npsScore = totalNotas > 0 ? Math.round(percPromotores - percDetratores) : null;

  return {
    nota1,
    nota2,
    nota3,
    nota4,
    nota5,
    totalNotas,
    getPercent,
    mediaNpsValor,
    detratores,
    passivos,
    promotores,
    percDetratores,
    percPassivos,
    percPromotores,
    npsRingBackground,
    mediaNpsLabel,
    npsScore
  };
}

function MovFitSatisfacaoUnidadeCard({ nomeUnidade, npsFonte }) {
  const m = computeNpsMetrics(npsFonte);

  return (
    <div className="bg-[color:var(--surface)] p-4 rounded-lg border border-[color:var(--border)] w-full flex flex-col">
      <div className="flex flex-col gap-4 mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
            <Smile className="h-5 w-5 text-emerald-500" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-lg sm:text-xl font-bold text-[color:var(--text-primary)] leading-tight break-words">
              {nomeUnidade}
            </p>
            <p className="text-sm font-medium text-[color:var(--text-primary)] mt-1 leading-tight">
              Satisfação dos clientes
            </p>
            <p className="text-xs text-[color:var(--text-muted)] mt-0.5">
              Distribuição das últimas avaliações
            </p>
          </div>
        </div>

        <div className="flex flex-row flex-wrap items-end gap-6">
          <div>
            <p className="text-xs text-[color:var(--text-muted)]">Nota média</p>
            <p className="text-3xl font-bold leading-none text-[color:var(--text-primary)]">
              {m.mediaNpsValor !== null ? m.mediaNpsValor.toFixed(2).replace('.', ',') : '--'}
            </p>
          </div>
          <div>
            <p className="text-xs text-[color:var(--text-muted)]">Total de avaliações</p>
            <p className="text-sm font-semibold leading-none text-[color:var(--text-primary)]">
              {m.totalNotas.toLocaleString('pt-BR')} avaliações
            </p>
          </div>
        </div>
      </div>

      <div className="mt-auto flex flex-col md:flex-row gap-6 items-stretch flex-1">
        <div className="flex-1 md:max-w-[200px] flex flex-col items-center md:items-start gap-4 mx-auto md:mx-0">
          <div
            className="relative w-28 h-28 sm:w-32 sm:h-32 rounded-full flex items-center justify-center shrink-0"
            style={{ backgroundImage: m.npsRingBackground }}
          >
            <div className="absolute inset-2 rounded-full bg-[color:var(--surface)] flex flex-col items-center justify-center text-center px-2">
              <span className="text-[10px] text-[color:var(--text-muted)] mb-0.5">NPS estimado</span>
              <span className="text-xl sm:text-2xl font-bold text-[color:var(--text-primary)] leading-none">
                {m.npsScore !== null ? `${m.npsScore > 0 ? '+' : ''}${m.npsScore}` : '--'}
              </span>
              {m.mediaNpsLabel && (
                <span className="mt-1 inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700">
                  {m.mediaNpsLabel}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 w-full text-xs">
            <div>
              <p className="text-[color:var(--text-muted)]">Promotores</p>
              <p className="font-semibold text-[color:var(--text-primary)]">
                {m.promotores.toLocaleString('pt-BR')}
              </p>
              <p className="text-[10px] text-[color:var(--text-muted)]">
                {m.totalNotas > 0 ? `${m.percPromotores.toFixed(1)}%` : '0%'}
              </p>
            </div>
            <div>
              <p className="text-[color:var(--text-muted)]">Passivos</p>
              <p className="font-semibold text-[color:var(--text-primary)]">
                {m.passivos.toLocaleString('pt-BR')}
              </p>
              <p className="text-[10px] text-[color:var(--text-muted)]">
                {m.totalNotas > 0 ? `${m.percPassivos.toFixed(1)}%` : '0%'}
              </p>
            </div>
            <div>
              <p className="text-[color:var(--text-muted)]">Detratores</p>
              <p className="font-semibold text-[color:var(--text-primary)]">
                {m.detratores.toLocaleString('pt-BR')}
              </p>
              <p className="text-[10px] text-[color:var(--text-muted)]">
                {m.totalNotas > 0 ? `${m.percDetratores.toFixed(1)}%` : '0%'}
              </p>
            </div>
          </div>
        </div>

        <div className="flex-[2] space-y-1.5 min-w-0">
          {[
            { label: '1', valor: m.nota1, cor: 'bg-rose-500' },
            { label: '2', valor: m.nota2, cor: 'bg-orange-400' },
            { label: '3', valor: m.nota3, cor: 'bg-amber-400' },
            { label: '4', valor: m.nota4, cor: 'bg-lime-400' },
            { label: '5', valor: m.nota5, cor: 'bg-emerald-500' }
          ].map((item) => {
            const percent = m.getPercent(item.valor);
            const width = m.totalNotas > 0 ? percent : 0;
            return (
              <div key={item.label} className="flex items-center gap-3">
                <div className="flex items-center justify-center w-7 h-7 rounded-full border border-[color:var(--border)] bg-[color:var(--surface-muted)] shrink-0">
                  <span className="text-xs font-semibold text-[color:var(--text-primary)]">{item.label}</span>
                </div>
                <div className="flex-1 h-2 rounded-full bg-[color:var(--surface-muted)] overflow-hidden min-w-0">
                  <div
                    className={`h-full rounded-full ${item.cor} transition-all`}
                    style={{ width: `${width}%` }}
                  />
                </div>
                <div className="flex flex-col items-end min-w-[60px] shrink-0">
                  <span className="text-[11px] font-medium text-[color:var(--text-primary)] leading-tight">
                    {item.valor.toLocaleString('pt-BR')}
                  </span>
                  <span className="text-[10px] text-[color:var(--text-muted)] leading-tight">
                    {m.totalNotas > 0 ? `${percent.toFixed(0)}%` : '0%'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const MovFitDashboard = ({ action, refreshKey = 0 }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [totalCancelamentosIa, setTotalCancelamentosIa] = useState(0);
  const [cancelamentosIaIndisponivel, setCancelamentosIaIndisponivel] = useState(false);
  const [aniversariantesMetricas, setAniversariantesMetricas] = useState(null);
  const [aniversariantesMesReferencia, setAniversariantesMesReferencia] = useState('');
  const [aniversariantesIndisponivel, setAniversariantesIndisponivel] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setCancelamentosIaIndisponivel(false);
      setAniversariantesIndisponivel(false);

      const [dashResult, cancelResult, automacoesResult] = await Promise.allSettled([
        api.get('/automacoes/dashboard', {
          params: { action }
        }),
        axios.get(MOVFIT_IA_CANCELAMENTOS_WEBHOOK, { timeout: 60000 }),
        axios.get(MOVFIT_DASHBOARD_AUTOMACOES_WEBHOOK, { timeout: 60000 })
      ]);

      if (dashResult.status === 'fulfilled') {
        const response = dashResult.value;
        const payload = response?.data?.data ?? response.data;
        let data = null;
        if (Array.isArray(payload) && payload.length > 0) {
          const firstItem = payload[0];
          if (firstItem?.total_geral) {
            data = firstItem.total_geral;
          } else {
            data = firstItem;
          }
        } else if (payload?.total_geral) {
          data = payload.total_geral;
        } else {
          data = payload;
        }
        setDashboardData(data);
      } else {
        const err = dashResult.reason;
        console.error('Erro ao carregar dashboard MOV Fit:', err);
        setError(
          err.response?.data?.message || err.message || 'Erro ao carregar dados'
        );
        setDashboardData(null);
      }

      if (cancelResult.status === 'fulfilled') {
        const body = cancelResult.value?.data;
        const n = parseTotalCancelamentosIa(body);
        setTotalCancelamentosIa(n ?? 0);
      } else {
        const err = cancelResult.reason;
        console.error('Erro ao carregar cancelamentos IA MOV Fit:', err);
        setTotalCancelamentosIa(0);
        setCancelamentosIaIndisponivel(true);
      }

      if (automacoesResult.status === 'fulfilled') {
        const parsed = parseDashboardAutomacoesMovFit(automacoesResult.value?.data);
        if (parsed?.aniversariantes) {
          setAniversariantesMetricas(parsed.aniversariantes);
          setAniversariantesMesReferencia(parsed.cobranca?.mes_atual || '');
        } else {
          setAniversariantesMetricas(null);
          setAniversariantesMesReferencia('');
          setAniversariantesIndisponivel(true);
        }
      } else {
        const err = automacoesResult.reason;
        console.error('Erro ao carregar templates de aniversariantes MOV Fit:', err);
        setAniversariantesMetricas(null);
        setAniversariantesMesReferencia('');
        setAniversariantesIndisponivel(true);
      }
    } catch (err) {
      console.error('Erro ao carregar dashboard MOV Fit:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [action]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData, refreshKey]);

  const npsPorUnidade =
    dashboardData?.nps_por_unidade && typeof dashboardData.nps_por_unidade === 'object'
      ? dashboardData.nps_por_unidade
      : {};

  const aniversariantesVisao = useMemo(() => {
    if (!aniversariantesMetricas) return null;
    const refMes = aniversariantesMesReferencia || mesReferenciaAtualYm();
    const mesLabel = formatMesReferencia(refMes);
    return {
      card1Titulo: 'Envios totais',
      card1ValorFmt: formatValorMetricaDisplay(aniversariantesMetricas.total),
      card1Sub: 'Templates de aniversariantes — consolidado',
      card2Titulo: mesLabel ? `Envios no mês (${mesLabel})` : 'Envios no mês atual',
      card2ValorFmt: formatValorMetricaDisplay(aniversariantesMetricas.mensal),
      card2Sub: refMes ? `Referência ${refMes}` : 'Conforme retorno do n8n'
    };
  }, [aniversariantesMetricas, aniversariantesMesReferencia]);

  if (loading) {
    return (
      <LoadingSpinner
        fullScreen={false}
        className="min-h-[240px] w-full"
        cardClassName="max-w-md w-full"
        label="Carregando dashboard MOV Fit"
        subtitle="Aguarde enquanto atualizamos os dados."
      />
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-[color:var(--text-primary)] mb-2">Erro ao carregar dados</h3>
        <p className="text-[color:var(--text-muted)] mb-4">{error}</p>
        <button 
          onClick={fetchDashboardData}
          className="inline-flex items-center px-4 py-2 bg-[color:var(--accent)] text-white rounded-md hover:opacity-90"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cabeçalho específico */}
      <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)] p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
              <Activity className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[color:var(--text-primary)]">MOV Fit</h1>
              <p className="text-[color:var(--text-muted)]">Dashboard de automações da MOV Fit - IA de Atendimento</p>
            </div>
          </div>
        </div>
      </div>

      {/* Cards de métricas - IA de Atendimento */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6">
        {/* Entraram em contato */}
        <div className="bg-[color:var(--surface)] p-5 rounded-lg border border-[color:var(--border)]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg shrink-0">
              <Phone className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-[color:var(--text-muted)] leading-tight">Entraram em contato</p>
              <p className="text-2xl font-bold text-[color:var(--text-primary)] mt-1 leading-none">
                {dashboardData?.total_clientes || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Finalizados pela IA */}
        <div className="bg-[color:var(--surface)] p-5 rounded-lg border border-[color:var(--border)]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg shrink-0">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-[color:var(--text-muted)] leading-tight">Finalizados pela IA</p>
              <p className="text-2xl font-bold text-[color:var(--text-primary)] mt-1 leading-none">
                {dashboardData?.encerrados || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Transferidos pela IA */}
        <div className="bg-[color:var(--surface)] p-5 rounded-lg border border-[color:var(--border)]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg shrink-0">
              <ArrowRightLeft className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-[color:var(--text-muted)] leading-tight">Transferidos pela IA</p>
              <p className="text-2xl font-bold text-[color:var(--text-primary)] mt-1 leading-none">
                {dashboardData?.transferidos || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Alunos */}
        <div className="bg-[color:var(--surface)] p-5 rounded-lg border border-[color:var(--border)]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg shrink-0">
              <Users className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-[color:var(--text-muted)] leading-tight">Alunos</p>
              <p className="text-2xl font-bold text-[color:var(--text-primary)] mt-1 leading-none">
                {dashboardData?.alunos_sim || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Alunos transferidos */}
        <div className="bg-[color:var(--surface)] p-5 rounded-lg border border-[color:var(--border)]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg shrink-0">
              <UserCheck className="h-5 w-5 text-[color:var(--accent)]" />
            </div>
            <div>
              <p className="text-xs font-medium text-[color:var(--text-muted)] leading-tight">Alunos transferidos</p>
              <p className="text-2xl font-bold text-[color:var(--text-primary)] mt-1 leading-none">
                {dashboardData?.alunos_transferidos || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Alunos não transferidos */}
        <div className="bg-[color:var(--surface)] p-5 rounded-lg border border-[color:var(--border)]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg shrink-0">
              <UserX className="h-5 w-5 text-rose-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-[color:var(--text-muted)] leading-tight">Alunos não transferidos</p>
              <p className="text-2xl font-bold text-[color:var(--text-primary)] mt-1 leading-none">
                {dashboardData?.alunos_nao_transferidos || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Custo mensal */}
        <div className="bg-[color:var(--surface)] p-5 rounded-lg border border-[color:var(--border)]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg shrink-0">
              <DollarSign className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-[color:var(--text-muted)] leading-tight">Custo mensal</p>
              <p className="text-2xl font-bold text-[color:var(--text-primary)] mt-1 leading-none">
                {dashboardData?.custo_mensal || 'R$ 0,00'}
              </p>
            </div>
          </div>
        </div>

        {/* Links de cobrança enviados */}
        <div className="bg-[color:var(--surface)] p-5 rounded-lg border border-[color:var(--border)]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg shrink-0">
              <Link2 className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-xs font-medium text-[color:var(--text-muted)] leading-tight">Links de cobrança enviados</p>
              <p className="text-2xl font-bold text-[color:var(--text-primary)] mt-1 leading-none">
                {dashboardData?.link_pagamento_enviado || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Solicitações de cancelamento (webhook n8n) */}
        <div
          className="bg-[color:var(--surface)] p-5 rounded-lg border border-[color:var(--border)]"
          title={
            cancelamentosIaIndisponivel
              ? 'Não foi possível carregar o total de solicitações de cancelamento.'
              : undefined
          }
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg shrink-0">
              <FileX2 className="h-5 w-5 text-rose-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-[color:var(--text-muted)] leading-tight">
                Solicitações de cancelamento
              </p>
              <p className="text-2xl font-bold text-[color:var(--text-primary)] mt-1 leading-none">
                {totalCancelamentosIa.toLocaleString('pt-BR')}
              </p>
              {cancelamentosIaIndisponivel && (
                <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">Dados indisponíveis</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Templates de aniversariantes */}
      <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)] p-6">
        <div className="flex gap-3 min-w-0 mb-5">
          <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg shrink-0 h-fit">
            <Gift className="h-5 w-5 text-[color:var(--accent)]" />
          </div>
          <div>
            <h4 className="text-base font-semibold text-[color:var(--text-primary)]">
              Templates de aniversariantes
            </h4>
            <p className="text-sm text-[color:var(--text-muted)] mt-0.5">
              Envios registrados pelo fluxo automático
            </p>
          </div>
        </div>

        {aniversariantesVisao ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)]/40 p-5 flex justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">
                  {aniversariantesVisao.card1Titulo}
                </p>
                <p className="mt-2 text-3xl font-bold text-[color:var(--text-primary)] tabular-nums">
                  {aniversariantesVisao.card1ValorFmt}
                </p>
                <p className="mt-2 text-xs text-[color:var(--text-muted)]">
                  {aniversariantesVisao.card1Sub}
                </p>
              </div>
              <div className="p-2.5 bg-[color:var(--surface)] rounded-xl shrink-0 self-start border border-[color:var(--border)]">
                <Gift className="h-5 w-5 text-[color:var(--accent)]" />
              </div>
            </div>
            <div className="rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)]/40 p-5 flex justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">
                  {aniversariantesVisao.card2Titulo}
                </p>
                <p className="mt-2 text-3xl font-bold text-[color:var(--text-primary)] tabular-nums">
                  {aniversariantesVisao.card2ValorFmt}
                </p>
                <p className="mt-2 text-xs text-[color:var(--text-muted)]">
                  {aniversariantesVisao.card2Sub}
                </p>
              </div>
              <div className="p-2.5 bg-[color:var(--surface)] rounded-xl shrink-0 self-start border border-[color:var(--border)]">
                <Calendar className="h-5 w-5 text-[color:var(--accent)]" />
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-[color:var(--text-muted)]">
            {aniversariantesIndisponivel
              ? 'Não foi possível carregar os envios de templates de aniversariantes.'
              : 'Nenhum dado de aniversariantes disponível.'}
          </p>
        )}
      </div>

      {/* Satisfação por unidade: um card por chave em `nps_por_unidade` do webhook */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {UNIDADES_SATISFACAO.map(({ key, nome }) => (
          <MovFitSatisfacaoUnidadeCard
            key={key}
            nomeUnidade={nome}
            npsFonte={npsPorUnidade[key]}
          />
        ))}
      </div>
    </div>
  );
};

export default MovFitDashboard;
